import React, { FC } from "react";
import { isNumberAttribute } from "interfaces/service";
import { Dropdown, Input, NumberInput, IOption } from "@evergis/ui";
import { AttributeType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { FieldType, fieldTypes } from "../../../../evergis/core/attributes/fieldTypes";
import { CalculationBlockContainer, InputRow } from "../styled";
import { useCurrentAttributesList } from "../hooks/useCurrentAttributesList";
import { AdvancedEditor } from "./AdvancedEditor";
import { AttributeOption } from "../../../../evergis/core/attributes/createAttributeOption";

export interface AttributeInfo {
  attributeOption?: AttributeOption;
  attributeValue?: string | number;
  newAttributeName?: string;
  newAttributeType?: IOption;
  templateValue?: string;
  resetAttributeValue?: VoidFunction;
}

interface CalculationBlockProps {
  attributeInfo: AttributeInfo;
  isNewAttribute?: boolean;
  advancedMode: boolean;
  layerName?: string;
  editorExpression?: string | number;
  formHandlers: {
    onChangeAttribute: (option: AttributeOption[]) => void;
    onChangeAttributeValue: (
      value: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | number,
    ) => void;
    onChangeAttributeName: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeAttributeType: (option: IOption[]) => void;
    handleSetTemplateValue: (value: string) => void;
    handleSetExpression: (value: string) => void;
  };
}

export const CalculationBlock: FC<CalculationBlockProps> = ({
  attributeInfo,
  isNewAttribute,
  layerName,
  advancedMode,
  formHandlers,
  editorExpression,
}) => {
  const { t } = useTranslation();
  const attributeOptions = useCurrentAttributesList(layerName);

  const typesList: FieldType[] = [
    { value: null, text: t("notChosen", { ns: "common" }), description: "" },
    ...fieldTypes,
  ].map(value => ({
    ...value,
    text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
  }));

  const processedTypesList = typesList.filter(
    value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType),
  );

  const { attributeOption, attributeValue, newAttributeName, newAttributeType, templateValue, resetAttributeValue } =
    attributeInfo;

  const {
    onChangeAttribute,
    onChangeAttributeValue,
    onChangeAttributeName,
    onChangeAttributeType,
    handleSetTemplateValue,
    handleSetExpression,
  } = formHandlers;

  const isNumberInput = isNewAttribute
    ? newAttributeType && isNumberAttribute(newAttributeType.value as AttributeType)
    : attributeOption && isNumberAttribute(attributeOption.type as AttributeType);

  return (
    <CalculationBlockContainer>
      <InputRow>
        <Dropdown
          label={t("featuresMultiEdit.targetAttribute", { ns: "dialogs" })}
          placeholder={t("featuresMultiEdit.chooseAttribute", { ns: "dialogs" })}
          width={"18.75rem"}
          options={attributeOptions.filter(Boolean)}
          onChange={onChangeAttribute}
          value={attributeOption?.value}
          zIndex={120}
        />
        {isNewAttribute && (
          <>
            <Input
              label={t("featuresMultiEdit.newAttributeName", { ns: "dialogs" })}
              onChange={onChangeAttributeName}
              value={newAttributeName}
              width={"13.5rem"}
            />
            <Dropdown
              label={t("featuresMultiEdit.newAttributeType", { ns: "dialogs" })}
              placeholder={t("notChosen", { ns: "common" })}
              width={"10.25rem"}
              options={processedTypesList}
              onChange={onChangeAttributeType}
              value={newAttributeType?.value}
              zIndex={120}
            />
          </>
        )}
      </InputRow>
      {advancedMode ? (
        <AdvancedEditor
          layerName={layerName}
          handleSetExpression={handleSetExpression}
          onChangeValue={onChangeAttributeValue}
          editorExpression={editorExpression}
          disabled={!attributeOption?.value}
        />
      ) : isNumberInput && !templateValue ? (
        <NumberInput
          label={t("featuresMultiEdit.value", { ns: "dialogs" })}
          placeholder={t("featuresMultiEdit.setAttributeValue", { ns: "dialogs" })}
          width={"18.75rem"}
          onChange={onChangeAttributeValue}
          value={attributeValue as number}
          zIndex={110}
          disabled={!attributeOption?.value}
        />
      ) : (
        <Input
          label={t("featuresMultiEdit.value", { ns: "dialogs" })}
          asChip={!!templateValue}
          placeholder={t("featuresMultiEdit.setAttributeValue", { ns: "dialogs" })}
          onChange={onChangeAttributeValue}
          value={attributeValue}
          width={"18.75rem"}
          disabled={!attributeOption?.value}
          onRemove={() => {
            handleSetTemplateValue(null);
            resetAttributeValue();
          }}
        />
      )}
    </CalculationBlockContainer>
  );
};
